import React, { useEffect, useMemo, useState } from "react";
import { Search, User } from "react-feather";
import dayjs from "dayjs";
import { useGetEmployeesQuery, useLazySearchEmployeeRemarksQuery } from "../employeesApi";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useCanViewRemarks } from "../useEmployeePermissions";
import GuardGradingPanel, { formatGrade, gradeBadgeClass } from "../components/GuardGradingPanel";

// Guard Remarks & Grading (Batch52, 2026-08-15) - ground-floor data capture
// for the future AI Guard Roster's candidate-scoring signal (memory:
// project_employee_remarks_grading.md): a searchable, dated incident/
// commendation log per guard, each entry optionally graded
// Excellent/Good/Average/Bad. Own top-level page (not folded into the
// Employee Master form) since it doubles as a cross-employee keyword search
// - the React-side equivalent of "SELECT * FROM Emp_History WHERE Remarks
// LIKE '%theft%'" the user cited as the legacy starting point.
//
// NOTE for whoever deploys next: per memory, the API code (Batch52/53) is
// live-verified against on-prem's DB and a locally-run instance only - it
// has NOT been published to IIS on-prem or remote yet, so these endpoints
// will 404 against the currently-deployed API until that publish happens.
const EmployeeRemarksPage = () => {
  const canView = useCanViewRemarks();
  const { selectedBranchId } = useBranchFilter();
  const { data: branches = [] } = useGetBranchesQuery();
  const branchCodeById = Object.fromEntries(branches.map((b) => [b.branchId, b.branchCode]));

  const [pickerSearchInput, setPickerSearchInput] = useState("");
  const [pickerSearch, setPickerSearch] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null); // { recordGuid, empName, empLastName, empCode, branchCode }

  const [keywordInput, setKeywordInput] = useState("");
  const [searchRemarks, { data: allSearchResults = [], isFetching: isSearching }] =
    useLazySearchEmployeeRemarksQuery();
  // The keyword search covers every branch the caller has; the hits carry their
  // branch, so they are narrowed to the header branch here (2026-10-02) - and
  // re-narrowed when it changes, without a new search.
  const searchResults =
    selectedBranchId == null ? allSearchResults : allSearchResults.filter((hit) => hit.branchId === selectedBranchId);

  useEffect(() => {
    const handle = setTimeout(() => setPickerSearch(pickerSearchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [pickerSearchInput]);

  /*
    Status filter, defaulting to ACTIVE (2026-09-18).

    This page originally had no status filter at all: on 2026-08-15 the
    `isActivate: true` was deliberately dropped so an ex-guard's conduct history
    stayed loggable and searchable after they left. That reasoning still holds -
    but with every leaver in the list by default, the picker filled up with
    inactive guards and the people you actually need were buried.

    So the capability is kept and only the DEFAULT changed: Active is the
    starting view, "Inactive" and "All" are one click away, and the keyword
    search across remarks below is untouched (it has always covered leavers
    regardless of this picker).
  */
  const [statusFilter, setStatusFilter] = useState("active");
  const isActivateParam = statusFilter === "all" ? undefined : statusFilter === "active";

  const { data: employees = [], isLoading: isLoadingEmployees } = useGetEmployeesQuery(
    {
      search: pickerSearch || undefined,
      branchId: selectedBranchId ?? undefined,
      ...(isActivateParam === undefined ? {} : { isActivate: isActivateParam }),
    },
    { skip: !canView }
  );

  // On "All", active first - a leaver should never sit above a serving guard.
  const pickerEmployees = useMemo(() => {
    if (statusFilter !== "all") return employees;
    return [...employees].sort((a, b) => Number(b.isActivate ? 1 : 0) - Number(a.isActivate ? 1 : 0));
  }, [employees, statusFilter]);

  const selectEmployee = (emp) => setSelectedEmployee(emp);

  const handleKeywordSearch = () => {
    if (!keywordInput.trim()) return;
    searchRemarks({ keyword: keywordInput.trim() });
  };

  const handleSearchResultClick = (hit) => {
    const match = employees.find((e) => e.recordGuid === hit.employeeRecordGuid);
    selectEmployee(
      match || {
        recordGuid: hit.employeeRecordGuid,
        empName: hit.empName,
        empLastName: hit.empLastName,
        empCode: hit.empCode,
        branchCode: branchCodeById[hit.branchId] || "",
      }
    );
  };

  if (!canView) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-info">
            Viewing guard remarks requires the Employees.ViewRemarks permission.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Guard Remarks &amp; Grading</h4>
              <h6>Dated conduct/performance notes per guard, with an optional Excellent–Bad grade</h6>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Search Remarks</h5>
            <div className="d-flex gap-2 mb-3" style={{ maxWidth: 480 }}>
              <input
                type="text"
                className="form-control"
                placeholder="Keyword, e.g. theft, commendation..."
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleKeywordSearch()}
              />
              <button type="button" className="btn btn-outline-primary" onClick={handleKeywordSearch} disabled={isSearching}>
                <Search size={14} className="me-1" /> Search
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Guard</th>
                      <th>Branch</th>
                      <th>Grade</th>
                      <th>Remark</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((hit) => (
                      <tr key={hit.recordGuid} role="button" onClick={() => handleSearchResultClick(hit)}>
                        <td>{hit.empName} {hit.empLastName} <span className="text-muted">({hit.empCode})</span></td>
                        <td>{branchCodeById[hit.branchId] || hit.branchId}</td>
                        <td><span className={`badge ${gradeBadgeClass(hit.grade)}`}>{formatGrade(hit.grade)}</span></td>
                        <td className="text-truncate" style={{ maxWidth: 320 }}>{hit.remarks}</td>
                        <td>{dayjs(hit.createdDate).format("DD MMM YYYY")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 mb-4">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="mb-3">Select Guard</h5>
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Search employee name or code"
                  value={pickerSearchInput}
                  onChange={(e) => setPickerSearchInput(e.target.value)}
                />
                <div className="btn-group w-100 mb-3" role="group">
                  {[
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                    { value: "all", label: "All" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`btn btn-sm ${statusFilter === opt.value ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setStatusFilter(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div style={{ maxHeight: 480, overflowY: "auto" }}>
                  {isLoadingEmployees && <p className="text-muted small">Loading...</p>}
                  {!isLoadingEmployees && pickerEmployees.length === 0 && (
                    <p className="text-muted small">
                      No {statusFilter === "all" ? "" : statusFilter} employees found.
                      {statusFilter === "active" && " Try Inactive or All if they have left."}
                    </p>
                  )}
                  {pickerEmployees.map((emp) => (
                    <button
                      type="button"
                      key={emp.recordGuid}
                      onClick={() => selectEmployee(emp)}
                      className={`btn w-100 text-start d-flex align-items-center gap-2 mb-1 ${
                        selectedEmployee?.recordGuid === emp.recordGuid ? "btn-primary" : "btn-light"
                      }`}
                    >
                      <User size={14} />
                      <span>
                        {emp.empName} {emp.empLastName}
                        {!Number(emp.isActivate) && (
                          <span className="badge badge-linedanger ms-2">Inactive</span>
                        )}
                        <span className="d-block small opacity-75">
                          {emp.empCode} · {emp.branchCode}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-8 mb-4">
            <div className="card h-100">
              <div className="card-body">
                {!selectedEmployee ? (
                  <p className="text-muted">Select a guard on the left, or click a search result above.</p>
                ) : (
                  <GuardGradingPanel employee={selectedEmployee} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeRemarksPage;
